import { format, subDays } from 'date-fns';
import { formatLocalDateKey } from '@ibk/shared-utils';
import { ConsentTransactionType } from '../../../../services/types';
const isSameDay = (firstDate, secondDate) => firstDate.getDate() === secondDate.getDate() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getFullYear() === secondDate.getFullYear();
const PIX_ICON = {
    credit: 'pixReceive',
    debit: 'pix',
};
const TRANSFERENCE_ICON = {
    credit: 'moveArrowDown',
    debit: 'moveArrowUp',
};
const BILL_ICON = {
    credit: 'pixReceive',
    debit: 'barcodeNeutral',
};
const sanitizeIdentifier = (value) => value.replace(/[^a-zA-Z0-9]/g, '');
const resolveTransactionDirection = (amount) => (amount >= 0 ? 'credit' : 'debit');
const resolveTransactionType = (transaction) => {
    const method = transaction.paymentData?.paymentMethod;
    if (method === 'PIX') {
        return ConsentTransactionType.PIX;
    }
    if (method === 'BOLETO') {
        return ConsentTransactionType.BILL;
    }
    if (transaction.category?.includes('Bank Slip') ||
        transaction.description?.includes('BOLETO') ||
        transaction.description?.includes('TITULO')) {
        return ConsentTransactionType.BILL;
    }
    return ConsentTransactionType.TRANSFERENCE;
};
const resolveTransactionIcon = (transaction) => {
    const direction = resolveTransactionDirection(transaction.amount);
    const type = resolveTransactionType(transaction);
    switch (type) {
        case ConsentTransactionType.TRANSFERENCE:
            return TRANSFERENCE_ICON[direction];
        case ConsentTransactionType.BILL:
            return BILL_ICON[direction];
        case ConsentTransactionType.PIX:
        default:
            return PIX_ICON[direction];
    }
};
const resolveTransactionSubtitle = (transaction) => transaction.description || '';
const resolveGroupLabelKey = (dateIso, todayLabel) => {
    const transactionDate = new Date(dateIso);
    if (!Number.isNaN(transactionDate.getTime()) && isSameDay(transactionDate, new Date())) {
        return todayLabel;
    }
    return transactionDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
};
export const getDefaultDateRange = () => {
    const today = new Date();
    const startDate = subDays(today, 30);
    return {
        from: format(startDate, 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
    };
};
const applyFiltersToTransaction = (transaction, filters) => {
    if (!transaction.date) {
        return false;
    }
    const transactionDate = new Date(transaction.date);
    if (Number.isNaN(transactionDate.getTime())) {
        return false;
    }
    const direction = resolveTransactionDirection(transaction.amount);
    if (filters.direction && direction !== filters.direction) {
        return false;
    }
    const type = resolveTransactionType(transaction);
    if (filters.type && type !== filters.type) {
        return false;
    }
    return transaction;
};
const manageTransactionsGroup = (dateKey, group, todayLabel) => {
    return {
        id: sanitizeIdentifier(dateKey),
        date: group.labelDate.toISOString(),
        label: resolveGroupLabelKey(group.labelDate.toISOString(), todayLabel),
        transactions: group.items
            .toSorted((first, second) => new Date(second.date).getTime() - new Date(first.date).getTime())
            .map(transaction => {
            return {
                id: transaction.id,
                title: transaction.description || '-',
                subtitle: resolveTransactionSubtitle(transaction),
                amount: Math.abs(transaction.amount),
                direction: resolveTransactionDirection(transaction.amount),
                icon: resolveTransactionIcon(transaction),
            };
        }),
    };
};
export const manageTransactionsList = (transactions, filters, translate) => {
    const grouped = new Map();
    let groupedTransactionsCount = 0;
    transactions.forEach(transaction => {
        if (!applyFiltersToTransaction(transaction, filters)) {
            return;
        }
        groupedTransactionsCount++;
        const transactionDate = new Date(transaction.date);
        const dateKey = formatLocalDateKey(transactionDate);
        const existingGroup = grouped.get(dateKey);
        if (existingGroup) {
            existingGroup.items.push(transaction);
            return;
        }
        grouped.set(dateKey, {
            labelDate: transactionDate,
            items: [transaction],
        });
    });
    return {
        total: groupedTransactionsCount,
        transactions: Array.from(grouped.entries())
            .map(([dateKey, group]) => manageTransactionsGroup(dateKey, group, translate('contabilidade-digital.account-transactions.section.today')))
            .toSorted((first, second) => new Date(second.date).getTime() - new Date(first.date).getTime()),
    };
};
